import React, { useEffect, useState } from "react";
import { api } from "@/utils/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { slugify } from "@/utils/slugify";
import { useSelector } from "react-redux";
import { trackUserAction } from "@/utils/analytics";
import { trackMetaSearch } from "@/utils/metaPixel";
import { normalizeServiceCategoriesResponse } from "@/vike/ssrHelpers";

type ServicesPageProps = {
  /** Categories from Vike SSR; undefined when loaded via SPA only. */
  initialCategories?: any[];
};

const ServicesPage = ({ initialCategories }: ServicesPageProps) => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState<any[]>(initialCategories ?? []);
  const [loading, setLoading] = useState(initialCategories === undefined);
  const navigate = useNavigate();
  const { user } = useSelector((state: { user }) => state.user || {});

  const navigateToServiceDetails = async(slug: string) =>{
   const slugNAme = slugify(slug)
   
   // Track service search/click
   trackUserAction('service_search', {
     service_name: slug,
     service_slug: slugNAme,
     user_type: user?.type || 'guest',
     source: 'services_page_click'
   });
   trackMetaSearch({
     search_string: slug,
     content_category: 'service',
   });
   
   navigate(`/service/${slugNAme}`)
  }
  useEffect(() => {
    let cancelled = false;

    const fetchCategories = async () => {
      const showFullPageLoader = categories.length === 0;
      if (showFullPageLoader) setLoading(true);

      try {
        const data = await api.getServiceCategories();
        if (!cancelled) {
          setCategories(normalizeServiceCategoriesResponse(data));
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600">
        <span className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></span>
        {t('loadingServices')}...
      </div>
    );
  }

  return (
    <div className="px-6 py-8 my-16 bg-home min-h-screen">
      {/* Back Button */}
      {/* <button
        onClick={() => navigate(-1)}
        className="flex items-center text-darkBlue hover:text-indigo-600 mb-6"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        {t('back')}
      </button> */}

      <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
        {t('availableServices')}
      </h1>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories
          .filter((category) => category.services?.length > 0)
          .map((category) => (
            <div
              key={category._id}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
            >
              <h2 className="text-xl font-semibold text-[#2D2D2D] mb-4">
                {category.name}
              </h2>
              <div className="flex flex-wrap gap-2">
                {category.services.map((service) => (
                  <div
                    key={service._id}
                    onClick={() =>
                      navigateToServiceDetails(
                        service.mlname?.[i18n.language] ||
                          service.mlname?.en ||
                          service.name
                      )
                    }
                    className="bg-lightBlue text-darkBlue px-3 py-1 rounded-md text-sm hover:shadow-sm cursor-pointer transition"
                  >
                    {service.name}
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ServicesPage;
